// Single in-flight refresh promise so that concurrent 401s trigger exactly one
// refresh-token request (all callers await the same promise).
let refreshTokenPromise = null;
let logoutInProgress = false;

export const baseFetch = async ({
  url,
  method = "GET",
  headers = {},
  params = {},
  body = null,
  signal,
}) => {
  const appUrl = import.meta.env.VITE_API_URL;
  if (params && Object.keys(params).length) {
    url += `?${new URLSearchParams(params)}`;
  }

  const fullUrl = `${appUrl}${url}`;
  return request({
    fullUrl,
    method,
    headers,
    body,
    signal,
    allowRefresh: true,
  });
};

/** Revoke a session without retrying with a rotated refresh token. */
export const revokeSession = ({ refreshToken, accessToken, signal }) =>
  fetch(`${import.meta.env.VITE_API_URL ?? ""}/v2/auth/logout`, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      ...(accessToken ? { Authorization: `Bearer ${accessToken}` } : {}),
    },
    body: JSON.stringify({ refreshToken }),
    keepalive: true,
    signal,
  }).then((response) => response.ok);

/**
 * Refresh the current pair before logout and keep other refreshes from racing
 * the revocation request. The caller must clear local state and call
 * `finishLogout` when the revocation attempt is complete.
 */
export const beginLogout = async () => {
  logoutInProgress = true;
  const originalSession = {
    accessToken: window.localStorage.getItem("token"),
    refreshToken: window.localStorage.getItem("refreshToken"),
  };

  if (originalSession.refreshToken) {
    await getNewAccessToken({ allowDuringLogout: true });
  }

  return {
    accessToken:
      window.localStorage.getItem("token") ?? originalSession.accessToken,
    refreshToken:
      window.localStorage.getItem("refreshToken") ??
      originalSession.refreshToken,
  };
};

export const finishLogout = () => {
  logoutInProgress = false;
};

// Performs the fetch and, on a 401 (expired/invalid access token), transparently
// refreshes the token and retries the original request exactly once.
const request = async ({
  fullUrl,
  method,
  headers,
  body,
  signal,
  allowRefresh,
}) => {
  const token = window.localStorage.getItem("token") ?? null;
  const fetchOptions = buildFetchOptions({
    method,
    headers,
    body,
    token,
    signal,
  });

  const response = await fetch(fullUrl, fetchOptions);

  // Detect auth failure by HTTP status, not by error-message string: the backend
  // returns varying messages on an expired access token (e.g. "jwt expired"),
  // so matching on text is unreliable. Only attempt a refresh when we actually
  // have a refresh token and haven't already retried.
  if (
    response.status === 401 &&
    allowRefresh &&
    window.localStorage.getItem("refreshToken")
  ) {
    const newAccessToken = await getNewAccessToken();
    if (newAccessToken) {
      return request({
        fullUrl,
        method,
        headers,
        body,
        signal,
        allowRefresh: false,
      });
    }
  }

  return handleResponse(response);
};

const buildFetchOptions = ({ method, headers, body, token, signal }) => {
  const isFormData = body instanceof FormData;
  return {
    method,
    headers: {
      ...headers,
      ...(isFormData ? {} : { "Content-Type": "application/json" }),
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
    },
    body: body ? (isFormData ? body : JSON.stringify(body)) : null,
    signal,
  };
};

const handleResponse = async (response) => {
  const contentType = response.headers.get("content-type");
  let data = contentType?.includes("application/json")
    ? await response.json()
    : await response.text();

  if (!response.ok) {
    // Preserve the existing contract: callers receive the error message string.
    throw (
      data?.message || data?.error || response.statusText || "Request failed"
    );
  }

  return data;
};

const getNewAccessToken = async ({ allowDuringLogout = false } = {}) => {
  if (logoutInProgress && !allowDuringLogout) {
    return null;
  }

  if (!refreshTokenPromise) {
    refreshTokenPromise = refreshAccessToken();
  }

  const currentPromise = refreshTokenPromise;
  try {
    return await currentPromise;
  } finally {
    if (refreshTokenPromise === currentPromise) {
      refreshTokenPromise = null;
    }
  }
};

const refreshAccessToken = async () => {
  const refreshToken = window.localStorage.getItem("refreshToken") ?? null;
  if (!refreshToken) {
    return null;
  }
  try {
    const response = await fetch(
      `${import.meta.env.VITE_API_URL}/v2/auth/tokens/refresh`,
      {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({ refreshToken }),
      },
    );
    const data = await response.json();
    if (!response.ok) {
      throw new Error(data?.message || "Failed to refresh token");
    }
    const tokens = data.data.tokens;
    window.localStorage.setItem("token", tokens.access.token);
    window.localStorage.setItem("refreshToken", tokens.refresh.token);
    return tokens.access.token;
  } catch (error) {
    // Refresh token is expired/invalid — the session is over. Clear all auth
    // state and send the user back to the entry route to re-authenticate.
    console.error("Refresh token error:", error);
    if (!logoutInProgress) {
      window.localStorage.clear();
      window.location.href = "/";
    }
    return null;
  }
};
